// AnalysisModal.js
import React, { useState } from 'react';
import './AnalysisModal.css';
import PipelineContractFields from '../shared/PipelineContractFields';
import {
  callerParameters,
  hasParameterContract,
  patientParameters,
} from '../shared/xomics-webserver-client';

function AnalysisModal({
  isOpen,
  onClose,
  onSubmit,
  pipeline,
  contractValues,
  onContractChange,
  selectedFiles,
  onFileChange,
  isSubmitting,
  uploadProgress,
  analysisLabel,
  sampleCodes,
  sampleCode,
  onSampleCodeChange
}) {
  const [sampleName, setSampleName] = useState('');

  if (!isOpen || !pipeline) return null;

  // Each file picker is one input the pipeline declares, at its position.
  const renderFileInput = (input) => {
    const slot = Number(input.position);
    const required = Number(input.is_required) === 1;
    const isMultiple = Number(input.accepts_multiple) === 1;
    const acceptStr = input.file_format ? `.${input.file_format},.${input.file_format}.gz` : undefined;
    const label = input.title || `Input ${slot}`;
    const description = input.content_kind_label || null;

    if (isMultiple) {
      const files = Array.isArray(selectedFiles[slot]) ? selectedFiles[slot] : [];

      const handleAddFiles = (e) => {
        const newFiles = Array.from(e.target.files);
        if (newFiles.length > 0) {
          onFileChange(slot, [...files, ...newFiles]);
        }
        e.target.value = '';
      };

      const handleRemoveFile = (index) => {
        onFileChange(slot, files.filter((_, i) => i !== index));
      };

      return (
        <div className="FMRP-modal-form-group" key={slot}>
          <label>
            {label}
            {required && <span className="FMRP-modal-required">*</span>}
            <span className="FMRP-modal-multi-hint"> (one or more files)</span>
          </label>
          <div className="FMRP-modal-file-input">
            <input
              type="file"
              className="FMRP-modal-input"
              accept={acceptStr}
              multiple
              onChange={handleAddFiles}
              disabled={isSubmitting}
              data-input-position={slot}
            />
          </div>
          {files.length > 0 && (
            <div className="FMRP-modal-file-list">
              {files.map((file, idx) => {
                const key = idx === 0 ? String(slot) : `${slot}_${idx + 1}`;
                const prog = uploadProgress[key];
                const uploading = prog !== undefined && prog < 100;
                return (
                  <div className="FMRP-modal-file-item" key={idx}>
                    <span className="FMRP-modal-file-name">
                      <i className="fas fa-file"></i> {file.name}
                    </span>
                    {!isSubmitting && (
                      <button
                        type="button"
                        className="FMRP-modal-file-remove"
                        onClick={() => handleRemoveFile(idx)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    {uploading && (
                      <div className="FMRP-modal-progress">
                        <div className="FMRP-modal-progress-bar" style={{ width: `${prog}%` }}></div>
                        <span className="FMRP-modal-progress-text">{prog}%</span>
                      </div>
                    )}
                    {prog === 100 && (
                      <span className="FMRP-modal-upload-complete">
                        <i className="fas fa-check"></i>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {description && <p className="FMRP-modal-field-desc">{description}</p>}
        </div>
      );
    }

    const file = selectedFiles[slot];
    const progress = uploadProgress[String(slot)];
    const isUploading = progress !== undefined && progress < 100;

    return (
      <div className="FMRP-modal-form-group" key={slot}>
        <label>
          {label}
          {required && <span className="FMRP-modal-required">*</span>}
        </label>
        <div className="FMRP-modal-file-input">
          <input
            type="file"
            className="FMRP-modal-input"
            accept={acceptStr}
            onChange={(e) => onFileChange(slot, e.target.files[0])}
            disabled={isSubmitting}
            data-input-position={slot}
          />
          {file && (
            <span className="FMRP-modal-file-name">
              <i className="fas fa-file"></i> {file.name}
            </span>
          )}
        </div>
        {description && <p className="FMRP-modal-field-desc">{description}</p>}
        {isUploading && (
          <div className="FMRP-modal-progress">
            <div className="FMRP-modal-progress-bar" style={{ width: `${progress}%` }}></div>
            <span className="FMRP-modal-progress-text">{progress}%</span>
          </div>
        )}
        {progress === 100 && (
          <span className="FMRP-modal-upload-complete">
            <i className="fas fa-check"></i> Uploaded
          </span>
        )}
      </div>
    );
  };

  const handleSubmit = () => {
    onSubmit(sampleName);
  };

  const inputs = [...(pipeline.inputs || [])].sort((left, right) => Number(left.position) - Number(right.position));
  const requiredInputs = inputs.filter((input) => Number(input.is_required) === 1);
  const optionalInputs = inputs.filter((input) => Number(input.is_required) !== 1);
  const contractReady = hasParameterContract(pipeline);
  const hasFields = contractReady
    && (callerParameters(pipeline).length > 0 || patientParameters(pipeline).length > 0);

  return (
    <div className="FMRP-modal-overlay" onClick={onClose}>
      <div className="FMRP-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="FMRP-modal-header">
          <h2>
            <i className="fas fa-cog"></i>
            Configure {analysisLabel}
          </h2>
          <button className="FMRP-modal-close-btn" onClick={onClose} disabled={isSubmitting}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="FMRP-modal-body">
          {/* Sample Name */}
          <div className="FMRP-modal-form-group">
            <label>
              Sample Name
              <span className="FMRP-modal-required">*</span>
            </label>
            <input
              type="text"
              className="FMRP-modal-input"
              value={sampleName}
              placeholder="Enter a name for this analysis..."
              onChange={(e) => setSampleName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {(sampleCodes?.length || 0) > 1 && (
            <div className="FMRP-modal-form-group">
              <label>
                Sample Type
                <span className="FMRP-modal-required">*</span>
              </label>
              <select
                className="FMRP-modal-select"
                value={sampleCode}
                onChange={(event) => onSampleCodeChange(event.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select...</option>
                {sampleCodes.map((code) => <option key={code} value={code}>{code}</option>)}
              </select>
            </div>
          )}

          {requiredInputs.length > 0 && (
            <>
              <h3 className="FMRP-modal-section-title">
                <i className="fas fa-file-upload"></i>
                Required Files
              </h3>
              {requiredInputs.map(renderFileInput)}
            </>
          )}

          {optionalInputs.length > 0 && (
            <>
              <h3 className="FMRP-modal-section-title">
                <i className="fas fa-file-alt"></i>
                Optional Files
              </h3>
              {optionalInputs.map(renderFileInput)}
            </>
          )}

          {!contractReady && (
            <p className="FMRP-modal-field-desc">
              The parameter declaration for this analysis is not available from the API yet, so it cannot be submitted.
            </p>
          )}

          {hasFields && (
            <>
              <h3 className="FMRP-modal-section-title">
                <i className="fas fa-sliders-h"></i>
                Parameters
              </h3>
              <PipelineContractFields
                pipeline={pipeline}
                values={contractValues}
                onChange={onContractChange}
                disabled={isSubmitting}
                idPrefix="fmrp"
                classes={{
                  group: 'FMRP-modal-form-group',
                  input: 'FMRP-modal-input',
                  select: 'FMRP-modal-select',
                  description: 'FMRP-modal-field-desc',
                  required: 'FMRP-modal-required',
                  label: '',
                }}
              />
            </>
          )}
        </div>

        <div className="FMRP-modal-footer">
          <button
            className="FMRP-modal-btn FMRP-modal-btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="FMRP-modal-btn FMRP-modal-btn-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !sampleName.trim() || !sampleCode || !contractReady}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-play"></i>
                Submit
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AnalysisModal;
